import { useFavoriteBuses } from "contexts/FavoriteBusesContext/FavoriteBusesContext";
import useMapViewConfiguration from "contexts/MapViewConfigurationContext/hooks/useMapViewConfiguration";
import useMapViewData from "contexts/MapViewDataContext/hooks/useMapViewData";
import { useMapViewRoutingMetadata } from "contexts/RoutingMetadataContext";
import useSelectedBuses from "contexts/SelectedBusesContext/useSelectedBuses";
import { useUserDataContext } from "contexts/UserDataContext/UserDataContext";
import { CircleLayout, CirclePaint } from "mapbox-gl";
import React from "react";
import { Layer, Source } from "react-map-gl";
import {
    getMapboxColorStepsForCapacity,
    getMapboxColorStepsForConstraintCount
} from "sharedStyles/capacityColorStyles";
import {
    ColorMode,
    DEFAULT_CONSTRAINT_COUNT_THRESHOLD
} from "types/busFilterTypes";
import FavoriteBusOverlay from "./FavoriteBusOverlay";
import {
    BUS_ID_KEY,
    CONSTRAINT_COUNT_KEY,
    getBusGeojson,
    NIRA_BUS_ID_KEY,
    SIZE_KEY
} from "./geoJsonUtils/busGeoJsonUtils";

export const BUS_LAYER_ID = "buses";

const BusSource: React.FC = () => {
    const { generator: generatorType } = useMapViewRoutingMetadata();
    const { selectedBusesAtLocation, selectedFacility } = useSelectedBuses();
    const { favorites, favoritesFilterOn } = useFavoriteBuses();
    const { scopedBuses, isLoadingBranches } = useMapViewData();
    const {
        busFiltersConfiguration: { busFilters }
    } = useMapViewConfiguration();
    const { colorMode, capacityThresholds, constraintCountThresholds } =
        busFilters;
    const { isColorblindMode } = useUserDataContext();

    const possibleSelectedBusIds = [
        selectedBusesAtLocation?.selectedBusId,
        selectedFacility?.fromBusId,
        selectedFacility?.toBusId
    ];

    const busIdEqualsCases = possibleSelectedBusIds.map((busId) => [
        "==",
        busId || null,
        ["get", BUS_ID_KEY]
    ]);
    const matchAnySelectedBusId = ["any", ...busIdEqualsCases];
    // We artificially slow down bus loading to give branches time to render.
    const busesToDisplay = isLoadingBranches ? [] : scopedBuses;

    const isConstraintCountMode = colorMode === ColorMode.CONSTRAINT_COUNT;
    const colorPropertyKey = isConstraintCountMode
        ? CONSTRAINT_COUNT_KEY
        : SIZE_KEY;
    const colorThresholds = isConstraintCountMode
        ? constraintCountThresholds ?? DEFAULT_CONSTRAINT_COUNT_THRESHOLD
        : capacityThresholds;

    const circlePaint: CirclePaint = {
        "circle-radius": ["case", matchAnySelectedBusId, 5, 4],
        "circle-color": "#FFFFFF",
        "circle-stroke-width": ["case", matchAnySelectedBusId, 7, 4],
        "circle-stroke-color": [
            "step",
            ["get", colorPropertyKey],
            ...(isConstraintCountMode
                ? getMapboxColorStepsForConstraintCount(
                      colorThresholds,
                      isColorblindMode
                  )
                : getMapboxColorStepsForCapacity(
                      colorThresholds,
                      isColorblindMode
                  ))
        ]
    };

    // Mapbox draws higher sort keys on top, so the selected bus's ring covers
    // any overlapping neighbors.
    const circleLayout: CircleLayout = {
        "circle-sort-key": ["case", matchAnySelectedBusId, 1, 0]
    };

    // Favorites draw as stars, so the circle layer excludes them.
    const isNotFavorite = [
        "!",
        ["in", ["get", NIRA_BUS_ID_KEY], ["literal", [...favorites]]]
    ];

    return (
        <>
            <Source
                id="bus-data"
                type="geojson"
                data={getBusGeojson(busesToDisplay, generatorType)}
            >
                <Layer
                    id={BUS_LAYER_ID}
                    type="circle"
                    filter={
                        favoritesFilterOn
                            ? ["all", matchAnySelectedBusId, isNotFavorite]
                            : isNotFavorite
                    }
                    paint={circlePaint}
                    layout={circleLayout}
                />
            </Source>
            <FavoriteBusOverlay
                busesToDisplay={busesToDisplay}
                possibleSelectedBusIds={possibleSelectedBusIds}
            />
        </>
    );
};

export default BusSource;
